import {
  Atom,
  ChevronRight,
  File,
  FileArchive,
  FileCode2,
  FileText,
  Folder,
  FolderOpen,
  Image as ImageIcon,
  LoaderCircle,
  RefreshCw,
  Search,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import type { FileVersion } from "@rnavoyager/contracts";
import { listWorkspaceFiles } from "./api";
import { sessionRelativeFilePath } from "./SessionFileReferences";
import { resolveWorkspacePreviewKind, WorkspaceFilePreviewDialog } from "./WorkspaceFilePreview";
import type { TaskEvent, WorkspaceFileEntry } from "./types";
interface FileTreeState {
  entriesByPath: Record<string, WorkspaceFileEntry[]>;
  expanded: Set<string>;
  busyPaths: Set<string>;
  error: string;
  filter: string;
  previewEntry: WorkspaceFileEntry | null;
  selectedPath: string;
  closePreview: () => void;
  setFilter: (value: string) => void;
  refresh: () => void;
  toggleDirectory: (entry: WorkspaceFileEntry) => void;
  selectFile: (entry: WorkspaceFileEntry) => void;
}

/** 两种资料来源共用文件树；公开案例显式提供只读目录读取器。 */
export function WorkspaceFilesPanel({
  sessionId,
  refreshKey,
  expanded,
  onToggle,
  ready,
  events = [],
  fileRequest,
  onFileRequestHandled,
  onReferenceFile,
  referenceDisabledReason,
  listFiles,
}: {
  sessionId: string;
  refreshKey: string;
  expanded: boolean;
  onToggle: () => void;
  ready: boolean;
  events?: readonly TaskEvent[];
  fileRequest?: { path: string } | null;
  onFileRequestHandled?: () => void;
  onReferenceFile?: (file: FileVersion) => void;
  referenceDisabledReason?: string;
  listFiles?: typeof listWorkspaceFiles;
}) {
  const fileTree = useWorkspaceFileTree(
    sessionId,
    refreshKey,
    expanded,
    ready,
    fileRequest,
    onFileRequestHandled,
    listFiles,
  );
  return (
    <>
      <section className="workspace-rail-card workspace-files-panel">
        <header className="workspace-file-heading">
          <button
            aria-controls="workspace-file-tree-content"
            aria-expanded={expanded}
            aria-label={expanded ? "收起文件树" : "展开文件树"}
            className="workspace-file-disclosure"
            onClick={onToggle}
          >
            <ChevronRight className={expanded ? "expanded" : ""} size={14} />
            <div>
              <p>
                <Folder size={13} />
                文件树
              </p>
            </div>
          </button>
          {expanded && (
            <button
              aria-label="刷新文件树"
              className="workspace-rail-icon-button"
              disabled={!ready}
              onClick={fileTree.refresh}
              title="刷新文件树"
            >
              <RefreshCw className={fileTree.busyPaths.size > 0 ? "spin" : ""} size={14} />
            </button>
          )}
        </header>
        {expanded && (
          <div className="workspace-file-tree-content" id="workspace-file-tree-content">
            <label className="workspace-file-filter">
              <Search size={14} />
              <input
                aria-label="筛选容器文件"
                placeholder="筛选文件"
                value={fileTree.filter}
                onChange={(event) => fileTree.setFilter(event.target.value)}
              />
            </label>
            {!ready ? (
              <p className="workspace-file-tree-empty" role="status">
                <LoaderCircle className="spin" size={15} />
                正在准备任务环境，文件将在就绪后显示。
              </p>
            ) : (
              <WorkspaceFileTree fileTree={fileTree} />
            )}
          </div>
        )}
      </section>
      {fileTree.previewEntry && (
        <WorkspaceFilePreviewDialog
          entry={fileTree.previewEntry}
          events={events}
          sessionId={sessionId}
          onClose={fileTree.closePreview}
          onReference={
            onReferenceFile
              ? (file) => {
                  onReferenceFile(file);
                  fileTree.closePreview();
                }
              : undefined
          }
          referenceDisabledReason={referenceDisabledReason}
        />
      )}
    </>
  );
}
function WorkspaceFileTree({ fileTree }: { fileTree: FileTreeState }) {
  const treeRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    treeRef.current?.querySelector("button.selected")?.scrollIntoView?.({ block: "nearest" });
  }, [fileTree.selectedPath]);
  const rootEntries = filterEntries(fileTree.entriesByPath["/project"] ?? [], fileTree.filter);
  return (
    <div ref={treeRef} className="workspace-file-tree" role="tree" aria-label="文件树">
      {fileTree.error && <p className="workspace-file-tree-error">{fileTree.error}</p>}
      {fileTree.busyPaths.has("/project") && rootEntries.length === 0 && (
        <p className="workspace-file-tree-empty">
          <LoaderCircle className="spin" size={15} /> 正在读取 /project…
        </p>
      )}
      {!fileTree.busyPaths.has("/project") && !fileTree.error && rootEntries.length === 0 && (
        <p className="workspace-file-tree-empty">目录中暂无文件</p>
      )}
      {renderFileEntries(rootEntries, 0, fileTree)}
      {fileTree.selectedPath && (
        <div className="workspace-selected-file">
          <span>已选择</span>
          <code title={fileTree.selectedPath}>{fileTree.selectedPath}</code>
        </div>
      )}
    </div>
  );
}

function renderFileEntries(entries: WorkspaceFileEntry[], depth: number, fileTree: FileTreeState) {
  return entries.map((entry) => {
    const isDirectory = entry.kind === "directory";
    const isExpanded = fileTree.expanded.has(entry.path);
    const children = filterEntries(fileTree.entriesByPath[entry.path] ?? [], fileTree.filter);
    return (
      <div className="workspace-file-branch" key={entry.path}>
        <button
          aria-expanded={isDirectory ? isExpanded : undefined}
          className={fileTree.selectedPath === entry.path ? "selected" : ""}
          role="treeitem"
          style={{ "--tree-depth": depth } as CSSProperties}
          title={isDirectory ? entry.path : `点击预览 ${entry.path}`}
          onClick={() =>
            isDirectory ? fileTree.toggleDirectory(entry) : fileTree.selectFile(entry)
          }
        >
          {isDirectory ? (
            <ChevronRight className={isExpanded ? "expanded" : ""} size={13} />
          ) : (
            <span className="workspace-file-chevron-spacer" />
          )}
          <WorkspaceFileIcon entry={entry} expanded={isExpanded} />
          <span className="workspace-file-name">{entry.name}</span>
          {!isDirectory && <small>{formatBytes(entry.size ?? 0)}</small>}
          {!isDirectory && resolveWorkspacePreviewKind(entry) !== "unsupported" && (
            <span aria-hidden="true" className="workspace-file-preview-indicator">
              预览
            </span>
          )}
          {fileTree.busyPaths.has(entry.path) && <LoaderCircle className="spin" size={12} />}
        </button>
        {isDirectory && isExpanded && (
          <div role="group">
            {!fileTree.busyPaths.has(entry.path) && children.length === 0 && (
              <p
                className="workspace-empty-directory"
                style={{ "--tree-depth": depth + 1 } as CSSProperties}
              >
                空目录
              </p>
            )}
            {renderFileEntries(children, depth + 1, fileTree)}
          </div>
        )}
      </div>
    );
  });
}

function WorkspaceFileIcon({ entry, expanded }: { entry: WorkspaceFileEntry; expanded: boolean }) {
  if (entry.kind === "directory") {
    return expanded ? <FolderOpen size={15} /> : <Folder size={15} />;
  }
  const previewKind = resolveWorkspacePreviewKind(entry);
  if (previewKind === "structure") return <Atom size={15} />;
  if (previewKind === "image") return <ImageIcon size={15} />;
  if (/\.(json|ya?ml|py|tsx?|jsx?)$/i.test(entry.name)) {
    return <FileCode2 size={15} />;
  }
  if (/\.(zip|gz|tar)$/i.test(entry.name)) {
    return <FileArchive size={15} />;
  }
  if (/\.(md|txt|log|csv|tsv|fa|fasta)$/i.test(entry.name)) {
    return <FileText size={15} />;
  }
  return <File size={15} />;
}

function useWorkspaceFileTree(
  sessionId: string,
  refreshKey: string,
  enabled: boolean,
  ready: boolean,
  fileRequest?: { path: string } | null,
  onFileRequestHandled?: () => void,
  listFiles: typeof listWorkspaceFiles = listWorkspaceFiles,
): FileTreeState {
  const [entriesByPath, setEntriesByPath] = useState<Record<string, WorkspaceFileEntry[]>>({});
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [busyPaths, setBusyPaths] = useState<Set<string>>(new Set());
  const [error, setError] = useState("");
  const [referenceError, setReferenceError] = useState("");
  const [filter, setFilter] = useState("");
  const [previewEntry, setPreviewEntry] = useState<WorkspaceFileEntry | null>(null);
  const [selectedPath, setSelectedPath] = useState("");
  const entriesRef = useRef<Record<string, WorkspaceFileEntry[]>>({});
  const expandedRef = useRef<Set<string>>(new Set());
  const refreshKeyRef = useRef(refreshKey);
  const sessionEpoch = useRef(0);

  const loadDirectory = useCallback(
    async (path: string, force = false) => {
      if (!ready) return;
      if (!force && entriesRef.current[path]) return entriesRef.current[path];
      const epoch = sessionEpoch.current;
      setBusyPaths((current) => new Set(current).add(path));
      setError("");
      try {
        const listing = await listFiles(sessionId, path);
        if (epoch !== sessionEpoch.current) return;
        entriesRef.current = {
          ...entriesRef.current,
          [path]: listing.entries,
        };
        setEntriesByPath(entriesRef.current);
        return listing.entries;
      } catch {
        if (epoch === sessionEpoch.current) setError("暂时无法读取容器文件。");
      } finally {
        if (epoch === sessionEpoch.current)
          setBusyPaths((current) => {
            const next = new Set(current);
            next.delete(path);
            return next;
          });
      }
    },
    [ready, sessionId, listFiles],
  );

  const refresh = useCallback(() => {
    if (!ready) return;
    setReferenceError("");
    const paths = new Set(["/project", ...Object.keys(entriesRef.current), ...expandedRef.current]);
    for (const path of paths) void loadDirectory(path, true);
  }, [loadDirectory, ready]);

  useEffect(() => {
    // 会话或环境可用性变化后，丢弃上一阶段的缓存与迟到响应。
    sessionEpoch.current += 1;
    entriesRef.current = {};
    expandedRef.current = new Set();
    refreshKeyRef.current = refreshKey;
    setEntriesByPath({});
    setExpanded(new Set());
    setPreviewEntry(null);
    setSelectedPath("");
    setBusyPaths(new Set());
    setError("");
    setReferenceError("");
    return () => {
      sessionEpoch.current += 1;
    };
  }, [ready, sessionId]);

  useEffect(() => {
    if (!fileRequest || !ready) return;
    let cancelled = false;
    setFilter("");
    setReferenceError("");
    setPreviewEntry(null);
    setSelectedPath("");
    async function reveal() {
      const relative = sessionRelativeFilePath(fileRequest!.path);
      if (!relative) {
        setReferenceError("文件引用不是有效的会话相对路径。");
        return;
      }
      const parts = relative.split("/");
      let parent = "/project";
      // 逐级读取真实目录项，不能凭摘要构造不存在的文件或媒体类型。
      for (const [index, part] of parts.entries()) {
        const entries = await loadDirectory(parent, true);
        if (cancelled) return;
        if (!entries) return;
        const path = `${parent}/${part}`;
        const entry = entries.find((entry) => entry.path === path);
        const last = index === parts.length - 1;
        if (!entry || entry.kind !== (last ? "file" : "directory")) {
          setReferenceError(`文件不存在或已不可访问：${relative}`);
          return;
        }
        if (last) {
          setSelectedPath(entry.path);
          setPreviewEntry(entry);
        } else {
          expandedRef.current = new Set(expandedRef.current).add(entry.path);
          setExpanded(expandedRef.current);
          parent = path;
        }
      }
    }
    void reveal().finally(() => {
      if (!cancelled) onFileRequestHandled?.();
    });
    return () => {
      cancelled = true;
    };
  }, [fileRequest, loadDirectory, onFileRequestHandled, ready]);

  useEffect(() => {
    if (!enabled || entriesRef.current["/project"]) return;
    void loadDirectory("/project", true);
  }, [enabled, loadDirectory]);

  useEffect(() => {
    if (refreshKey === refreshKeyRef.current) return;
    refreshKeyRef.current = refreshKey;
    if (enabled) refresh();
  }, [enabled, refresh, refreshKey]);

  const toggleDirectory = useCallback(
    (entry: WorkspaceFileEntry) => {
      const opening = !expandedRef.current.has(entry.path);
      const next = new Set(expandedRef.current);
      if (opening) next.add(entry.path);
      else next.delete(entry.path);
      expandedRef.current = next;
      setExpanded(next);
      if (opening) void loadDirectory(entry.path);
    },
    [loadDirectory],
  );

  const closePreview = useCallback(() => setPreviewEntry(null), []);
  const selectFile = useCallback((entry: WorkspaceFileEntry) => {
    setReferenceError("");
    setSelectedPath(entry.path);
    setPreviewEntry(entry);
  }, []);

  return {
    entriesByPath,
    expanded,
    busyPaths,
    error: referenceError || error,
    filter,
    previewEntry,
    selectedPath,
    closePreview,
    setFilter,
    refresh,
    toggleDirectory,
    selectFile,
  };
}

function filterEntries(entries: WorkspaceFileEntry[], filter: string) {
  const query = filter.trim().toLocaleLowerCase();
  if (!query) return entries;
  return entries.filter((entry) => entry.name.toLocaleLowerCase().includes(query));
}

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}
